import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  RotateCcw,
  RefreshCw
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { formatDate, formatPercentage } from '../utils/formatters';
import toast from 'react-hot-toast';

interface TestReport {
  id: string;
  status: string;
  totalDocuments: number;
  successfulDocuments: number;
  failedDocuments: number;
  overallAccuracy: number;
  startedAt: string;
  completedAt: string;
  invoiceStats: any;
  receiptStats: any;
  fieldStats: any;
  commonErrors: Array<{
    fieldPath: string;
    count: number;
    percentage: number;
  }>;
  testResults: Array<{
    documentId: string;
    filename: string;
    documentType: string;
    isSuccess: boolean;
    errorCount: number;
    errors: any[];
    extractedData?: any;
    confidenceScore: number;
    processingTime: number;
    reliabilityScore?: number;
  }>;
}

// Helper function to reconstruct extracted data from errors
const reconstructDataFromErrors = (goldenData: any, errors: any[]) => {
  if (!goldenData || !errors) return goldenData;

  // Deep clone golden data
  const reconstructed = JSON.parse(JSON.stringify(goldenData));

  // Apply errors to reconstructed data
  errors.forEach(error => {
    const path = error.field_path;
    const extractedValue = error.extracted_value;

    // Navigate to the correct path and set the extracted value
    const pathParts = path.split(/[.[\]]+/).filter((part: string) => part !== '');
    let current = reconstructed;

    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }

    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart) {
      current[lastPart] = extractedValue;
    }
  });

  return reconstructed;
};

export const TestResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<TestReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'errors' | 'review' | 'methodology'>('overview');
  const [errorReviews, setErrorReviews] = useState<Record<string, 'valid' | 'invalid' | null>>({});
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [documentImageUrl, setDocumentImageUrl] = useState<string | null>(null);
  const [contextData, setContextData] = useState<any>(null);
  const [goldenData, setGoldenData] = useState<any>(null);
  const [methodology, setMethodology] = useState<any>(null);
  const [documentCache, setDocumentCache] = useState<Record<string, any>>({});
  const [retryProgress, setRetryProgress] = useState<{[key: string]: any}>({});

  useEffect(() => {
    if (id) {
      fetchReport(id);
    }
  }, [id]);

  const fetchReport = async (testId: string) => {
    try {
      console.log('Fetching test report...', { testId });
      const response = await apiClient.get(`/api/tests/${testId}/report`);
      console.log('Test report received:', response.data.test);
      
      // Debug: Log first few failed results to see the errors
      const failedResults = response.data.test.testResults.filter((r: any) => !r.isSuccess);
      if (failedResults.length > 0) {
        console.log('First 3 failed results:', failedResults.slice(0, 3));
      }
      
      setReport(response.data.test);
    } catch (error) {
      console.error('Failed to fetch test report:', error);
    } finally {
      setLoading(false);
    }
  };

  const markErrorAsValid = (documentId: string, fieldPath: string, isValid: boolean) => {
    const key = `${documentId}-${fieldPath}`;
    const newReview: 'valid' | 'invalid' = isValid ? 'valid' : 'invalid';
    console.log(`Marking error as ${newReview}:`, { documentId, fieldPath, key });

    setErrorReviews(prev => {
      const updated: Record<string, 'valid' | 'invalid' | null> = {
        ...prev,
        [key]: newReview
      };
      console.log('Updated reviews:', updated);
      return updated;
    });
  };

  const getErrorReview = (documentId: string, fieldPath: string) => {
    const key = `${documentId}-${fieldPath}`;
    const review = errorReviews[key] || null;
    // console.log(`Getting review for ${key}:`, review); // Uncomment for debugging
    return review;
  };

  const saveReviews = async () => {
    if (!id || Object.keys(errorReviews).length === 0) return;

    try {
      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));

      await apiClient.post(`/api/tests/${id}/reviews`, { reviews: errorReviews });

      // After saving, move to next unreviewed document
      const nextDocument = getNextUnreviewedDocument();
      if (nextDocument) {
        // Add small delay before switching documents
        setTimeout(() => {
          setSelectedDocument(nextDocument);
        }, 200);
      } else {
        // All documents reviewed, go back to overview
        setSelectedDocument(null);
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('Rate limited during save, retrying in 3 seconds...');
        setTimeout(() => saveReviews(), 3000);
      } else {
        console.error('Failed to save reviews:', error);
      }
    }
  };

  const getNextUnreviewedDocument = () => {
    if (!report) return null;

    const documentsWithErrors = report.testResults.filter(result => result.errorCount > 0);

    for (const result of documentsWithErrors) {
      if (result.documentId === selectedDocument) continue; // Skip current document

      // Check if all errors for this document are reviewed
      const hasUnreviewedErrors = result.errors?.some(error => {
        const review = getErrorReview(result.documentId, error.field_path);
        return review === null;
      });

      if (hasUnreviewedErrors) {
        return result.documentId;
      }
    }

    return null;
  };

  const isDocumentFullyReviewed = (documentId: string) => {
    const result = report?.testResults.find(r => r.documentId === documentId);
    if (!result || !result.errors || result.errors.length === 0) return false;

    return result.errors.every(error => {
      const review = getErrorReview(documentId, error.field_path);
      return review !== null;
    });
  };

  const loadDocumentImage = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/api/documents/${documentId}/file`, {
        responseType: 'blob'
      });
      const blob = response.data;
      const imageUrl = URL.createObjectURL(blob);
      setDocumentImageUrl(imageUrl);
    } catch (error) {
      console.error('Failed to load document image:', error);
      setDocumentImageUrl(null);
    }
  };

  const handleRetry = async (documentId: string, filename: string) => {
    if (!window.confirm(`Chcete znovu zpracovat dokument "${filename}"?`)) {
      return;
    }

    try {
      const response = await apiClient.post(`/api/documents/${documentId}/retry`);
      const { retryId } = response.data;
      
      toast.success('Dokument byl odeslán ke zpracování');
      
      // Start listening to retry progress
      const eventSource = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/retry-progress/${retryId}`);
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'retry_progress') {
            setRetryProgress(prev => ({
              ...prev,
              [documentId]: data
            }));
            
            // Show completion message
            if (data.status === 'completed') {
              toast.success(`Dokument "${filename}" byl úspěšně zpracován!`);
              console.log('Retry completed, refreshing test results...', { documentId, testId: id });
              // Refresh test results
              if (id) {
                fetchReport(id);
              }
              eventSource.close();
              
              // Clear progress after delay
              setTimeout(() => {
                setRetryProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[documentId];
                  return newProgress;
                });
              }, 3000);
            } else if (data.status === 'error') {
              toast.error(`Chyba při zpracování dokumentu "${filename}": ${data.error}`);
              // Refresh test results
              if (id) {
                fetchReport(id);
              }
              eventSource.close();
              
              // Clear progress after delay
              setTimeout(() => {
                setRetryProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[documentId];
                  return newProgress;
                });
              }, 3000);
            }
          }
        } catch (error) {
          console.error('Error parsing retry progress:', error);
        }
      };
      
      eventSource.onerror = () => {
        eventSource.close();
      };
      
    } catch (error) {
      console.error('Retry error:', error);
      toast.error('Chyba při odesílání dokumentu ke zpracování');
    }
  };

  const handleCheckStatus = async (documentId: string) => {
    try {
      const response = await apiClient.get(`/api/documents/${documentId}/status`);
      const { document, lyxApiStatus } = response.data;
      
      let statusMessage = `Dokument: ${document.status}`;
      if (lyxApiStatus?.data?.processing_status) {
        statusMessage += `\nAPI status: ${lyxApiStatus.data.processing_status}`;
      }
      
      toast.success(statusMessage);
    } catch (error) {
      console.error('Status check error:', error);
      toast.error('Chyba při kontrole stavu dokumentu');
    }
  };

  // Load document image and context data when a document is selected
  useEffect(() => {
    if (!selectedDocument) {
      setDocumentImageUrl(null);
      setContextData(null);
      return;
    }

    const selectedResult = report?.testResults.find(r => r.documentId === selectedDocument);
    if (!selectedResult?.filename?.toLowerCase().endsWith('.pdf')) {
      loadDocumentImage(selectedDocument);
    }

    // Load context data and golden data with caching
    const fetchDocumentData = async () => {
      try {
        // Check cache first
        if (documentCache[selectedDocument]) {
          const cached = documentCache[selectedDocument];
          setGoldenData(cached.goldenData);

          // Still process context data from test results
          const selectedResult = report?.testResults.find(r => r.documentId === selectedDocument);

          if (selectedResult && selectedResult.extractedData) {
            setContextData(selectedResult.extractedData);
          } else if (selectedResult?.errors && selectedResult.errors.length > 0) {
            const reconstructedData = reconstructDataFromErrors(cached.goldenData, selectedResult.errors);
            setContextData(reconstructedData);
          } else {
            setContextData(cached.initialExtractionData);
          }
          return;
        }

        // Add delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 300));

        const response = await apiClient.get(`/api/documents/${selectedDocument}`);
        const documentData = response.data.document;

        // Cache the document data
        setDocumentCache(prev => ({
          ...prev,
          [selectedDocument]: documentData
        }));

        setGoldenData(documentData.goldenData);

        // For context data, use the extracted data from the current test result
        const selectedResult = report?.testResults.find(r => r.documentId === selectedDocument);

        if (selectedResult && selectedResult.extractedData) {
          // Use the extracted data from the test result (current API extraction)
          setContextData(selectedResult.extractedData);
        } else if (selectedResult?.errors && selectedResult.errors.length > 0) {
          // Reconstruct extracted data from errors
          const reconstructedData = reconstructDataFromErrors(documentData.goldenData, selectedResult.errors);
          setContextData(reconstructedData);
        } else {
          // Fallback to initial extraction data if no test result data
          setContextData(documentData.initialExtractionData);
        }
      } catch (error: any) {
        if (error.response?.status === 429) {
          // Rate limited - try again after longer delay
          console.warn('Rate limited, retrying in 2 seconds...');
          setTimeout(() => fetchDocumentData(), 2000);
        } else {
          console.error('Failed to fetch document data:', error);
          setContextData(null);
          setGoldenData(null);
        }
      }
    };

    fetchDocumentData();

    return () => {
      if (documentImageUrl) {
        URL.revokeObjectURL(documentImageUrl);
        setDocumentImageUrl(null);
      }
    };
  }, [selectedDocument, report, documentCache, documentImageUrl]);

  // Load methodology when methodology tab is accessed
  useEffect(() => {
    if (activeTab === 'methodology' && !methodology) {
      fetchMethodology();
    }
  }, [activeTab, methodology]);

  const fetchMethodology = async () => {
    try {
      const response = await apiClient.get('/api/tests/methodology');
      setMethodology(response.data.methodology);
    } catch (error) {
      console.error('Failed to fetch methodology:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-3 gap-6 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-7xl mx-auto text-center py-12">
        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Test report not found</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Přehled', icon: TrendingUp },
    { id: 'documents', name: 'Dokumenty', icon: FileText },
    { id: 'errors', name: 'Chyby', icon: AlertTriangle },
    { id: 'review', name: 'Detailní porovnání', icon: Eye },
    { id: 'methodology', name: 'Metodika hodnocení', icon: AlertCircle }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center space-x-4">
          <Link to="/history" className="btn-secondary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to History
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Test Results</h1>
            <p className="text-gray-600 mt-2">
              Test #{report.id.slice(-8)} • {formatDate(report.startedAt, true)}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900">{report.totalDocuments}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Successful</p>
              <p className="text-2xl font-bold text-green-600">{report.successfulDocuments}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-red-600">{report.failedDocuments}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Accuracy</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatPercentage(report.overallAccuracy)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="h-4 w-4 mr-2 inline" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Document Type Breakdown */}
          {(report.invoiceStats || report.receiptStats) && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Accuracy by Document Type
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {report.invoiceStats && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatPercentage(report.invoiceStats.accuracy)}
                    </div>
                    <div className="text-sm text-blue-800">
                      Invoices ({report.invoiceStats.successful}/{report.invoiceStats.total})
                    </div>
                  </div>
                )}
                {report.receiptStats && (
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {formatPercentage(report.receiptStats.accuracy)}
                    </div>
                    <div className="text-sm text-green-800">
                      Receipts ({report.receiptStats.successful}/{report.receiptStats.total})
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Common Errors */}
          {report.commonErrors && report.commonErrors.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Most Common Errors
              </h3>
              <div className="space-y-3">
                {report.commonErrors.slice(0, 5).map((error, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{error.fieldPath}</p>
                      <p className="text-sm text-gray-600">{error.count} documents affected</p>
                    </div>
                    <span className="text-red-600 font-medium">
                      {formatPercentage(error.percentage)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Document Results</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Document</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Type</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Reliability</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Errors</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Confidence</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Time</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {report.testResults.map((result) => (
                  <tr key={result.documentId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <span className="font-medium text-gray-900">{result.filename}</span>
                          {/* Show retry progress */}
                          {retryProgress[result.documentId] && (
                            <div className="mt-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${retryProgress[result.documentId].progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {retryProgress[result.documentId].progress}%
                                </span>
                              </div>
                              <p className="text-xs text-blue-600 mt-1">
                                {retryProgress[result.documentId].step}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${result.documentType === 'invoice'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                        }`}>
                        {result.documentType}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      {result.isSuccess ? (
                        <span className="badge-success">Success</span>
                      ) : (
                        <span className="badge-error">Failed</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {result.reliabilityScore !== null && result.reliabilityScore !== undefined ? (
                        <span className={`font-medium ${result.reliabilityScore >= 90 ? 'text-green-600' :
                            result.reliabilityScore >= 70 ? 'text-blue-600' :
                              result.reliabilityScore >= 50 ? 'text-yellow-600' :
                                'text-red-600'
                          }`}>
                          {result.reliabilityScore.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={result.errorCount > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {result.errorCount}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-600">{result.confidenceScore}%</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-600">{result.processingTime}ms</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Retry button for all documents (allows re-testing) */}
                        <button
                          onClick={() => handleRetry(result.documentId, result.filename)}
                          className={`text-sm ${!result.isSuccess ? 'btn-warning' : 'btn-secondary'}`}
                          title="Znovu zpracovat"
                          disabled={retryProgress[result.documentId] && retryProgress[result.documentId].status !== 'completed' && retryProgress[result.documentId].status !== 'error'}
                        >
                          <RefreshCw className={`h-4 w-4 ${retryProgress[result.documentId] && retryProgress[result.documentId].status !== 'completed' && retryProgress[result.documentId].status !== 'error' ? 'animate-spin' : ''}`} />
                        </button>
                        
                        {/* Status check button */}
                        <button
                          onClick={() => handleCheckStatus(result.documentId)}
                          className="btn-info text-sm"
                          title="Zkontrolovat stav"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Error Details</h3>

          {report.testResults.filter(r => !r.isSuccess).length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-500">No errors found! All documents passed validation.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {report.testResults
                .filter(result => !result.isSuccess)
                .map((result) => (
                  <div key={result.documentId} className="border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{result.filename}</h4>
                      <span className="badge-error">{result.errorCount} errors</span>
                    </div>

                    <div className="space-y-2">
                      {result.errors?.map((error, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium text-gray-700">{error.field_path}:</span>
                          <div className="ml-4 text-gray-600">
                            <div>Expected: <span className="font-mono bg-gray-100 px-1 rounded">{error.golden_value}</span></div>
                            <div>Got: <span className="font-mono bg-gray-100 px-1 rounded">{error.extracted_value}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'review' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Detailní porovnání chyb</h3>
            <p className="text-gray-600 mb-6">
              Zkontrolujte každou chybu a označte, zda je rozdíl oprávněný nebo skutečná chyba API.
            </p>

            {!selectedDocument ? (
              <div>
                {(() => {
                  const unreviewedDocuments = report.testResults
                    .filter(result => !result.isSuccess && !isDocumentFullyReviewed(result.documentId));

                  const reviewedDocuments = report.testResults
                    .filter(result => !result.isSuccess && isDocumentFullyReviewed(result.documentId));

                  return (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">Dokumenty k review:</h4>
                        <div className="text-sm text-gray-600">
                          {unreviewedDocuments.length} nehodnocených • {reviewedDocuments.length} hotových
                        </div>
                      </div>

                      {unreviewedDocuments.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                          <p className="text-gray-500">Všechny dokumenty s chybami už byly ohodnocené!</p>
                          {reviewedDocuments.length > 0 && (
                            <p className="text-sm text-gray-400 mt-2">
                              Můžete se podívat na již ohodnocené dokumenty níže.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {unreviewedDocuments.map((result) => (
                            <button
                              key={result.documentId}
                              onClick={() => setSelectedDocument(result.documentId)}
                              className="w-full text-left p-4 border border-orange-200 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium text-gray-900">{result.filename}</h5>
                                  <p className="text-sm text-orange-600">{result.errorCount} chyb k review</p>
                                </div>
                                <Eye className="h-5 w-5 text-orange-600" />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {reviewedDocuments.length > 0 && (
                        <div className="mt-6">
                          <h5 className="font-medium text-gray-900 mb-3">Již ohodnocené dokumenty:</h5>
                          <div className="space-y-2">
                            {reviewedDocuments.map((result) => (
                              <button
                                key={result.documentId}
                                onClick={() => setSelectedDocument(result.documentId)}
                                className="w-full text-left p-4 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{result.filename}</h5>
                                    <p className="text-sm text-green-600">✓ Ohodnoceno ({result.errorCount} chyb)</p>
                                  </div>
                                  <Eye className="h-5 w-5 text-green-600" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div>
                {(() => {
                  const selectedResult = report.testResults.find(r => r.documentId === selectedDocument);
                  if (!selectedResult) return null;

                  return (
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">{selectedResult.filename}</h4>
                          <p className="text-gray-600">{selectedResult.errorCount} chyb k review</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setSelectedDocument(null)}
                            className="btn-secondary"
                          >
                            Zpět na seznam
                          </button>

                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6 mb-6">
                        {/* Document preview */}
                        <div className="bg-white rounded-lg shadow-sm border">
                          <div className="p-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Náhled dokumentu</h3>
                          </div>
                          <div className="p-4" style={{ height: '600px' }}>
                            <div className="h-full border border-gray-300 rounded overflow-hidden">
                              {selectedResult.filename?.toLowerCase().endsWith('.pdf') ? (
                                <iframe
                                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/${selectedResult.documentId}/file-preview?token=${localStorage.getItem('auth_token')}`}
                                  className="w-full h-full"
                                  title="Document Preview"
                                  onError={(e) => console.error('PDF load error:', e)}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                  {documentImageUrl ? (
                                    <img
                                      src={documentImageUrl}
                                      alt="Document Preview"
                                      className="max-w-full max-h-full object-contain"
                                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                                      onLoad={() => console.log('Image loaded successfully from blob')}
                                    />
                                  ) : (
                                    <div className="text-center p-4">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                      <p className="text-gray-500">Načítání náhledu...</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Review statistics */}
                        <div className="bg-white rounded-lg shadow-sm border">
                          <div className="p-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Statistiky hodnocení</h3>
                          </div>
                          <div className="p-4">
                            {(() => {
                              const totalErrors = selectedResult.errors?.length || 0;
                              const reviewedErrors = selectedResult.errors?.filter(error =>
                                getErrorReview(selectedDocument, error.field_path) !== null
                              ).length || 0;
                              const validErrors = selectedResult.errors?.filter(error =>
                                getErrorReview(selectedDocument, error.field_path) === 'valid'
                              ).length || 0;
                              const invalidErrors = selectedResult.errors?.filter(error =>
                                getErrorReview(selectedDocument, error.field_path) === 'invalid'
                              ).length || 0;

                              return (
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-3 bg-gray-50 rounded">
                                      <div className="text-2xl font-bold text-gray-900">{totalErrors}</div>
                                      <div className="text-sm text-gray-600">Celkem chyb</div>
                                    </div>
                                    <div className="text-center p-3 bg-blue-50 rounded">
                                      <div className="text-2xl font-bold text-blue-600">{reviewedErrors}</div>
                                      <div className="text-sm text-blue-600">Hodnoceno</div>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-3 bg-green-50 rounded">
                                      <div className="text-2xl font-bold text-green-600">{validErrors}</div>
                                      <div className="text-sm text-green-600">Oprávněné rozdíly</div>
                                    </div>
                                    <div className="text-center p-3 bg-red-50 rounded">
                                      <div className="text-2xl font-bold text-red-600">{invalidErrors}</div>
                                      <div className="text-sm text-red-600">Skutečné chyby API</div>
                                    </div>
                                  </div>

                                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                    <div className="text-center">
                                      <div className="text-lg font-medium text-yellow-800">
                                        Pokrok: {Math.round((reviewedErrors / totalErrors) * 100)}%
                                      </div>
                                      <div className="w-full bg-yellow-200 rounded-full h-2 mt-2">
                                        <div
                                          className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${(reviewedErrors / totalErrors) * 100}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Bulk actions */}
                      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">Rychlé akce pro všechny chyby:</h4>
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => {
                              selectedResult.errors?.forEach(error => {
                                markErrorAsValid(selectedDocument, error.field_path, true);
                              });
                            }}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <ThumbsUp className="h-4 w-4 mr-2" />
                            Všechny jako oprávněné rozdíly
                          </button>

                          <button
                            onClick={() => {
                              selectedResult.errors?.forEach(error => {
                                markErrorAsValid(selectedDocument, error.field_path, false);
                              });
                            }}
                            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <ThumbsDown className="h-4 w-4 mr-2" />
                            Všechny jako chyby API
                          </button>

                          <button
                            onClick={() => {
                              selectedResult.errors?.forEach(error => {
                                setErrorReviews(prev => {
                                  const newReviews = { ...prev };
                                  delete newReviews[`${selectedDocument}-${error.field_path}`];
                                  return newReviews;
                                });
                              });
                            }}
                            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Resetovat všechny
                          </button>

                          <button
                            onClick={saveReviews}
                            disabled={Object.keys(errorReviews).length === 0}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Uložit a přejít na další
                          </button>
                        </div>
                      </div>

                      {/* Side-by-side data comparison */}
                      <div className="mb-6 bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-xl p-4">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Kompletní porovnání dat
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {/* Golden Data */}
                          <div>
                            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-t-lg text-sm font-medium flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Zlatá data (správné)
                            </div>
                            <div className="bg-white rounded-b-lg border border-t-0 border-green-200 p-3 max-h-96 overflow-y-auto">
                              {(() => {
                                const renderGoldenValue = (value: any, path: string = '', level: number = 0): React.ReactNode => {
                                  if (value === null || value === undefined) {
                                    return <span className="text-gray-400">∅</span>;
                                  }

                                  if (Array.isArray(value)) {
                                    return (
                                      <div className="ml-2">
                                        <span className="text-green-600 text-xs">Array [{value.length}]:</span>
                                        {value.map((item, idx) => {
                                          const itemPath = `${path}[${idx}]`;
                                          const hasError = selectedResult.errors?.some(err => err.field_path.startsWith(itemPath));
                                          return (
                                            <div key={idx} className={`ml-2 mt-1 p-1 border-l-2 ${hasError ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}>
                                              <div className="text-xs text-gray-500">#{idx + 1}:</div>
                                              {renderGoldenValue(item, itemPath, level + 1)}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }

                                  if (typeof value === 'object') {
                                    return (
                                      <div className={`${level > 0 ? 'ml-2' : ''} text-xs`}>
                                        {Object.entries(value).map(([key, val]) => {
                                          const fieldPath = path ? `${path}.${key}` : key;
                                          const hasError = selectedResult.errors?.some(err => err.field_path === fieldPath);
                                          return (
                                            <div key={key} className={`${hasError ? 'bg-green-50 -mx-1 px-1 rounded' : ''}`}>
                                              <span className="text-gray-600">{key}:</span> {renderGoldenValue(val, fieldPath, level + 1)}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }

                                  const hasError = selectedResult.errors?.some(err => err.field_path === path);

                                  return (
                                    <span className={`${hasError ? 'bg-green-200 px-1 rounded font-medium' : ''}`}>
                                      {String(value)}
                                      {hasError && <span className="ml-1 text-green-700 text-xs">✓</span>}
                                    </span>
                                  );
                                };

                                return goldenData ? (
                                  <div className="text-xs font-mono space-y-1">
                                    {Object.entries(goldenData).map(([key, value]) => (
                                      <div key={key}>
                                        <span className="font-semibold">{key}:</span> {renderGoldenValue(value, key)}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-2 text-gray-500 text-xs">Načítání zlatých dat...</div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Extracted Data */}
                          <div>
                            <div className="bg-red-100 text-red-800 px-3 py-1 rounded-t-lg text-sm font-medium flex items-center">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Vytěžená data (API)
                            </div>
                            <div className="bg-white rounded-b-lg border border-t-0 border-red-200 p-3 max-h-96 overflow-y-auto">
                              {(() => {
                                const renderExtractedValue = (value: any, path: string = '', level: number = 0): React.ReactNode => {
                                  if (value === null || value === undefined) {
                                    return <span className="text-gray-400">∅</span>;
                                  }

                                  if (Array.isArray(value)) {
                                    return (
                                      <div className="ml-2">
                                        <span className="text-red-600 text-xs">Array [{value.length}]:</span>
                                        {value.map((item, idx) => {
                                          const itemPath = `${path}[${idx}]`;
                                          const hasError = selectedResult.errors?.some(err => err.field_path.startsWith(itemPath));
                                          return (
                                            <div key={idx} className={`ml-2 mt-1 p-1 border-l-2 ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                                              <div className="text-xs text-gray-500">#{idx + 1}:</div>
                                              {renderExtractedValue(item, itemPath, level + 1)}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }

                                  if (typeof value === 'object') {
                                    return (
                                      <div className={`${level > 0 ? 'ml-2' : ''} text-xs`}>
                                        {Object.entries(value).map(([key, val]) => {
                                          const fieldPath = path ? `${path}.${key}` : key;
                                          const hasError = selectedResult.errors?.some(err => err.field_path === fieldPath);
                                          return (
                                            <div key={key} className={`${hasError ? 'bg-red-50 -mx-1 px-1 rounded' : ''}`}>
                                              <span className="text-gray-600">{key}:</span> {renderExtractedValue(val, fieldPath, level + 1)}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }

                                  const hasError = selectedResult.errors?.some(err => err.field_path === path);

                                  return (
                                    <span className={`${hasError ? 'bg-red-200 px-1 rounded font-medium' : ''}`}>
                                      {String(value)}
                                      {hasError && <span className="ml-1 text-red-600 text-xs">✗</span>}
                                    </span>
                                  );
                                };

                                return contextData ? (
                                  <div className="text-xs font-mono space-y-1">
                                    {Object.entries(contextData).map(([key, value]) => (
                                      <div key={key}>
                                        <span className="font-semibold">{key}:</span> {renderExtractedValue(value, key)}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-2 text-gray-500 text-xs">Načítání vytěžených dat...</div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2 text-xs text-gray-600 flex items-center justify-center">
                          <span className="bg-red-200 px-2 py-0.5 rounded mr-2">Zvýrazněno</span>
                          = rozdílné hodnoty
                        </div>
                      </div>

                      {/* Enhanced error comparison list */}
                      <div className="space-y-3">
                        {selectedResult.errors?.map((error, index) => {
                          const review = getErrorReview(selectedDocument, error.field_path);
                          return (
                            <div
                              key={index}
                              className={`border rounded-xl shadow-sm transition-all duration-300 hover:shadow-md ${review === 'valid' ? 'border-green-300 bg-gradient-to-r from-green-50 to-green-100' :
                                  review === 'invalid' ? 'border-red-300 bg-gradient-to-r from-red-50 to-red-100' :
                                    'border-gray-200 bg-white hover:border-blue-300'
                                }`}
                            >
                              {/* Field path header */}
                              <div
                                className="px-4 py-3 border-b border-gray-200 bg-gray-50 cursor-pointer flex items-center justify-between"
                                onClick={() => {
                                  if (review === 'valid') {
                                    markErrorAsValid(selectedDocument, error.field_path, false);
                                  } else {
                                    markErrorAsValid(selectedDocument, error.field_path, true);
                                  }
                                }}
                              >
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 rounded-full mr-3 ${review === 'valid' ? 'bg-green-500' :
                                      review === 'invalid' ? 'bg-red-500' :
                                        'bg-gray-300'
                                    }`}></div>
                                  <span className="font-semibold text-gray-900">{error.field_path}</span>
                                  {review === 'valid' && (
                                    <span className="ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Oprávněný rozdíl
                                    </span>
                                  )}
                                  {review === 'invalid' && (
                                    <span className="ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Chyba API
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2">
                                  {review === 'valid' ? (
                                    <ThumbsUp className="h-5 w-5 text-green-600" />
                                  ) : review === 'invalid' ? (
                                    <ThumbsDown className="h-5 w-5 text-red-600" />
                                  ) : (
                                    <span className="text-xs text-gray-500">Klikněte pro označení</span>
                                  )}
                                </div>
                              </div>

                              {/* Value comparison */}
                              <div className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Golden data (correct) */}
                                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                    <div className="flex items-center mb-2">
                                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                                      <span className="font-medium text-green-800">Zlatá data (správné)</span>
                                    </div>
                                    <div className="bg-white rounded p-3 border border-green-300">
                                      <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap break-all">
                                        {error.golden_value || <span className="text-gray-400 italic">(prázdné)</span>}
                                      </pre>
                                    </div>
                                  </div>

                                  {/* API extracted data */}
                                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                    <div className="flex items-center mb-2">
                                      <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                                      <span className="font-medium text-red-800">Vytěžená data (API)</span>
                                    </div>
                                    <div className="bg-white rounded p-3 border border-red-300">
                                      <pre className="font-mono text-sm text-gray-800 whitespace-pre-wrap break-all">
                                        {error.extracted_value || <span className="text-gray-400 italic">(prázdné)</span>}
                                      </pre>
                                    </div>
                                  </div>
                                </div>

                                {/* Visual difference indicator */}
                                <div className="mt-3 text-center">
                                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-600">
                                    <span className="font-medium">Rozdíl: </span>
                                    <span className="ml-2 font-mono">
                                      "{error.golden_value || '∅'}" → "{error.extracted_value || '∅'}"
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-6 space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium mb-1">Jak hodnotit rozdíly:</p>
                              <ul className="space-y-1">
                                <li><strong>Chyba API:</strong> API vytěžilo špatné nebo neúplné údaje</li>
                                <li><strong>Oprávněný rozdíl:</strong> Rozdíl je způsobený legitimními faktory (formátování, zaokrouhlování, atd.)</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {(() => {
                          const reviewedErrors = selectedResult.errors?.filter(error =>
                            getErrorReview(selectedDocument, error.field_path) !== null
                          ).length || 0;
                          const totalErrors = selectedResult.errors?.length || 0;
                          const allReviewed = reviewedErrors === totalErrors && totalErrors > 0;

                          return (
                            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900">
                                  Hodnocení: {reviewedErrors} / {totalErrors} chyb
                                </p>
                                <p className="text-sm text-gray-600">
                                  {allReviewed ? 'Všechny chyby byly hodnoceny!' : 'Dokončete hodnocení všech chyb'}
                                </p>
                              </div>

                              {allReviewed && (
                                <div className="flex items-center space-x-3">
                                  <div className="text-green-600 font-medium">
                                    ✓ Hotovo
                                  </div>
                                  <button
                                    onClick={async () => {
                                      console.log('=== FRONTEND REVIEW SAVE ===');
                                      console.log('Report ID:', report.id);
                                      console.log('Selected document:', selectedDocument);
                                      console.log('All error reviews:', errorReviews);

                                      const documentReviews = Object.fromEntries(
                                        Object.entries(errorReviews).filter(([key]) => key.startsWith(selectedDocument))
                                      );

                                      console.log('Filtered document reviews:', documentReviews);
                                      console.log('URL will be:', `/api/tests/${report.id}/reviews`);

                                      try {
                                        console.log('Making API call...');
                                        const response = await apiClient.post(`/api/tests/${report.id}/reviews`, {
                                          reviews: documentReviews
                                        });
                                        console.log('API Response:', response.data);

                                        if (response.data.success) {
                                          alert(`Hodnocení úspěšně uloženo!
Uloženo ${response.data.reviewCount} hodnocení.
Nová úspěšnost: ${response.data.newAccuracy.toFixed(1)}%
Úspěšné dokumenty: ${response.data.successfulDocuments}/${response.data.totalDocuments}`);

                                          // Reload report to show updated data
                                          await fetchReport(report.id);
                                        }
                                      } catch (error) {
                                        console.error('Failed to save reviews:', error);
                                        alert('Chyba při ukládání hodnocení. Zkuste to znovu.');
                                      }
                                    }}
                                    className="btn-primary"
                                  >
                                    Uložit hodnocení
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'methodology' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Metodika výpočtu spolehlivosti dokumentu
            </h3>

            {methodology ? (
              <div className="space-y-6">
                {/* Přehled kategorií */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Kategorie chyb a jejich váhy</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h5 className="font-medium text-red-800 mb-2">Kritické (váha 8-10)</h5>
                      <p className="text-sm text-red-700">Základní identifikační údaje</p>
                      <p className="text-xs text-red-600 mt-1">Číslo dokumentu, celková částka, IČO, datum vystavení</p>
                    </div>
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h5 className="font-medium text-yellow-800 mb-2">Důležité (váha 5-7)</h5>
                      <p className="text-sm text-yellow-700">Podstatné obchodní údaje</p>
                      <p className="text-xs text-yellow-600 mt-1">Název dodavatele, DIČ, částky DPH, datumy</p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h5 className="font-medium text-blue-800 mb-2">Menší (váha 1-3)</h5>
                      <p className="text-sm text-blue-700">Doplňkové informace</p>
                      <p className="text-xs text-blue-600 mt-1">Adresy, bankovní údaje, symboly</p>
                    </div>
                  </div>
                </div>

                {/* Váhy polí */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Detailní váhy polí</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-900">Pole</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-900">Váha</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-900">Kategorie</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-900">Popis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {methodology.weights
                          .filter((w: any) => w.weight > 0)
                          .sort((a: any, b: any) => b.weight - a.weight)
                          .map((weight: any, index: number) => (
                            <tr key={index} className="border-b border-gray-100">
                              <td className="py-2 px-3 text-sm font-mono text-gray-700">{weight.fieldPath}</td>
                              <td className="py-2 px-3 text-sm">
                                <span className={`font-medium ${weight.category === 'critical' ? 'text-red-600' :
                                    weight.category === 'important' ? 'text-yellow-600' :
                                      'text-blue-600'
                                  }`}>
                                  {weight.weight}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-sm">
                                <span className={`badge text-xs ${weight.category === 'critical' ? 'bg-red-100 text-red-800' :
                                    weight.category === 'important' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-blue-100 text-blue-800'
                                  }`}>
                                  {weight.category}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-sm text-gray-600">{weight.description}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Prahy úspěšnosti */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Prahy hodnocení</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <div className="text-green-800 font-medium">≥ {methodology.thresholds.excellent}%</div>
                      <div className="text-sm text-green-700">Výborná spolehlivost</div>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-blue-800 font-medium">{methodology.thresholds.good}% - {methodology.thresholds.excellent - 1}%</div>
                      <div className="text-sm text-blue-700">Dobrá spolehlivost</div>
                    </div>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="text-yellow-800 font-medium">{methodology.thresholds.acceptable}% - {methodology.thresholds.good - 1}%</div>
                      <div className="text-sm text-yellow-700">Přijatelná spolehlivost</div>
                    </div>
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <div className="text-red-800 font-medium">&lt; {methodology.thresholds.acceptable}%</div>
                      <div className="text-sm text-red-700">Nízká spolehlivost</div>
                    </div>
                  </div>
                </div>

                {/* Příklady výpočtů */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Příklady výpočtů</h4>
                  <div className="space-y-3">
                    {methodology.examples.map((example: any, index: number) => (
                      <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-2">{example.scenario}</h5>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-mono bg-white px-2 py-1 rounded border">{example.calculation}</span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">{example.result}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Popis algoritmu */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-md font-medium text-blue-800 mb-3">Jak funguje výpočet</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
                    <li>Spočítáme celkovou váhu všech chyb v dokumentu</li>
                    <li>Spočítáme maximální možnou váhu pro všechna pole s chybami</li>
                    <li>Základní skóre = ((max_váha - celková_váha_chyb) / max_váha) × 100</li>
                    <li>Aplikujeme confidence score z API jako faktor</li>
                    <li>Dokument je úspěšný pouze bez chyb, spolehlivost je nezávislá metrika</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-500">Načítání metodiky...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};